import { InferType, nullToUndefined, q } from "groqd";

import {
    CodeQuery,
    CustomTableQuery,
    ImageQuery,
    NoteQuery,
    RouteQuery,
    TableQuery
} from "../../../lib/sanity/types";
import { runQuery } from "../../../lib/sanityClient";

const shape = q("*")
    .filterByType("article")
    .filter("hidden != true")
    // .filter('!(_id in path("drafts.**"))')
    .filter("slug.current == $slug")
    .filter('coalesce(language, "en") == $locale')
    // .slice(0)
    .grab({
        _id: q.string(),
        publishedAt: q.string(),
        title: q.string(),
        seo: q
            .object({
                metaDescription: q.string().nullable().optional(),
                metaTitle: q.string().nullable().optional(),
                keywords: q.string().nullable().optional(),
                synonyms: q.string().nullable().optional()
            })
            .nullable(),
        slug: q.object({
            current: q.string(),
            _type: q.literal("slug")
        }),
        wordCount: [
            "round(length(pt::text(body)) / $meanWordCharacterCount)",
            q.number()
        ],
        readingTime: [
            "round(length(pt::text(body)) / $meanWordCharacterCount / $wpm)",
            q.number()
        ],
        tags: q("tags[]", { isArray: true })
            .deref()
            .grab({
                name: ["coalesce(title[$locale], name)", q.string()],
                slug: q.object({
                    current: q.string(),
                    _type: q.literal("slug")
                }),
                path: [`"/tags/" + slug.current`, q.string()]
            }),
        type: ["type", q.string().nullable()],

        language: ['coalesce(language, "en")', q.string().nullable()],
        excerpt: [
            `array::join(string::split((pt::text(body)), "")[0..255], "") + "..."`,
            q.string()
        ],
        path: [`"/articles/" + slug.current`, q.string()],
        nextPost: q("*")
            .filterByType("article")
            .filter("hidden != true")
            .filter('!(_id in path("drafts.**"))')
            .filter('coalesce(language, "en") == $locale')
            .filter("dateTime(publishedAt) > dateTime(^.publishedAt)")
            .order("published asc")
            .slice(0)
            .grab({
                title: q.string(),
                path: [`"/articles/" + slug.current`, q.string()]
            })
            .nullable(),
        previousPost: q("*")
            .filterByType("article")
            .filter("hidden != true") // Not hidden
            .filter('!(_id in path("drafts.**"))') // Not a draft
            .filter('coalesce(language, "en") == $locale') // in the same language as the current post
            .filter("dateTime(publishedAt) < dateTime(^.publishedAt)") // published before the current post
            .order("published desc") // sorted by published date descending
            .slice(0) // first one
            .grab({
                title: q.string(),
                path: [`"/articles/" + slug.current`, q.string()]
            })
            .nullable(),

        // todo: Add all body types as needed
        // NOTE: USE the GROQD CMS extension to work out the types
        body: q("body[]", { isArray: true }).select({
            "_type == 'route'": ["@->", RouteQuery],
            "_type == 'imageIm'": ["@->", ImageQuery],
            "_type == 'image'": ["@", ImageQuery],
            "_type == 'reference'": ["@->", q.contentBlock()],
            "_type == 'note'": ["@", NoteQuery],
            "_type == 'table'": ["@", TableQuery],
            "_type == 'customTable'": ["@", CustomTableQuery],
            "_type == 'code'": ["@", CodeQuery],
            "_type == 'block'": ["@", q.contentBlock()],
            default: {
                _key: q.string(),
                _type: ['"unsupported"', q.literal("unsupported")],
                unsupportedType: ["_type", q.string()]
            }
        }),

        // canonical is the current article ALWAYS
        canonical: q.select(
            nullToUndefined({
                '$locale == "en"': [`"/articles/" + slug.current`, q.string()],
                '$locale != "en"': [
                    `"/" + $locale + "/articles/" + slug.current`,
                    q.string()
                ]
            })
        ),

        translatedContent: q("*")
            .filterByType("translation.metadata")
            .filter("references(^._id)")
            .slice(0)
            .grab({
                translations: q("translations[]", { isArray: true }).grab({
                    value: q("value")
                        .deref()
                        .grab({
                            locale: ['coalesce(language, "en")', q.string()],
                            title: q.string(),
                            path: [`"/articles/" + slug.current`, q.string()],
                            href: q.select({
                                'language == "en"': [
                                    `"/articles/" + slug.current`,
                                    q.string()
                                ],
                                'language != "en"': [
                                    `"/" + $locale + "/articles/" + slug.current`,
                                    q.string()
                                ]
                            }),
                            hrefLang: ['coalesce(language, "en")', q.string()],
                            canonical: [
                                'coalesce(language, "en") == $locale',
                                q.boolean()
                            ],
                            fallback: ["false", q.literal(false)]
                        })
                })
            })
            .nullable()
    });

export type ArticleDetail = InferType<typeof shape.schema>;
export type TypeOfArray<T extends unknown[]> = T extends (infer U)[] ? U : never;
export type ArticleDetailType = TypeOfArray<ArticleDetail>;

const filterDataToSingleItem = (data: ArticleDetail, preview: boolean) =>
    // eslint-disable-next-line no-underscore-dangle
    data.length > 1 && preview
        ? // eslint-disable-next-line no-underscore-dangle
          data.filter((item) => item._id.startsWith(`drafts.`)).pop()
        : // eslint-disable-next-line no-underscore-dangle
          data.filter((item) => !item._id.startsWith(`drafts.`)).pop();

export const getArticleDetails = async (slug: string, locale = "en", preview = false) =>
    runQuery(shape, {
        slug,
        locale,
        wpm: 180,
        meanWordCharacterCount: 5
    }).then((data) => filterDataToSingleItem(data, preview));
